<script setup lang="ts">
    import { computed, inject, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { createEvent, copyEvent } from '@cwd/Views/Event/Event.access';
    import { bsAlert } from '@shared/components/bsAlerts';
    import Modal from '@shared/components/Modal.vue';
    import EventList from './EventList.vue';
    import { type Event as EventItem } from './EventTypes';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@shared/components/Form/Form';
    import Loading from '@shared/components/Loading.vue';
    import NewPreview from '@cwd/Views/Surveys/NewPreview.vue';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { UserEventService } from '@shared/Root/UserEvent.service';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const userEventService = injectorAccess.injector.get(UserEventService);

    const state = reactive<{
        isLoading: boolean;
        isGlobalTemplate: boolean;
        eventId: string | null;
        showSettingsModal: boolean;
        form: FormProps;
    }>({
        isLoading: false,
        isGlobalTemplate: false,
        eventId: null,
        showSettingsModal: false,
        form: {
            data: {
                CopyEventDetails: {
                    modelValue: true,
                    checkbox: {
                        label: $Resources.EventDetails,
                        switchStyle: 'standard',
                        disabled: true,
                    },
                },
                CopySignUpForm: {
                    modelValue: true,
                    checkbox: {
                        label: $Resources.EventNavEntryForm,
                        switchStyle: 'standard',
                    },
                },
                CopyConfirmationMailing: {
                    modelValue: true,
                    checkbox: {
                        label: $Resources.EventConfirmationsHeader,
                        switchStyle: 'standard',
                    },
                },
                CopySpeakers: {
                    modelValue: true,
                    checkbox: {
                        label: $Resources.EventCopySpeakersHeader,
                        switchStyle: 'standard',
                    },
                },
                CopySponsors: {
                    modelValue: true,
                    checkbox: {
                        label: $Resources.EventCopySponsorsHeader,
                        switchStyle: 'standard',
                    },
                },
                CopyTags: {
                    modelValue: true,
                    checkbox: {
                        label: $Resources.EventCopyTagsHeader,
                        switchStyle: 'standard',
                    },
                },
            },
        },
    });

    const iframeSrc = computed(() => {
        if (!state.eventId) return '';
        return combineUrlSegments(contextAccess.state.currentAccount!.PublicEventUrl.PublicHost, 'Events', '' + state.eventId, '?preview=true');
    });

    const setEventId = (eventId: string) => {
        if (state.isLoading) return;
        state.eventId = eventId;
    };

    const onCreateEvent = async () => {
        if (state.isLoading) return;
        state.isLoading = true;

        await createEvent()
            .then((data: EventItem) => {
                userEventService.registerUserEvent('Events', 'Created draft event', data.Name);
                window.location.replace('/Events/' + data.EventId + '?new=true');
            })
            .catch(() => {
                bsAlert({ content: 'Event could not be created.', type: 'danger' });
                state.isLoading = false;
            });
    };

    const onCopyEvent = (event: Event, formData: any) => {
        if (state.isLoading || !state.eventId) return;
        state.isLoading = true;

        copyEvent(state.eventId, formData)
            .then((data: EventItem) => {
                window.location.replace('/Events/' + data.EventId);
            })
            .catch(() => {
                bsAlert({ title: $Resources.ErrorCouldNotCopyEventHeading, content: $Resources.ErrorCouldNotCopyEventBody, type: 'danger' });
                state.isLoading = false;
            });
    };
</script>

<template>
    <div class="p-lg-r p-lg-l">
        <header role="banner">
            <div class="d-flex justify-content-between align-items-baseline">
                <h1>{{ $Resources.NewEventHeading }}</h1>
            </div>

            <p class="m-lg-b">
                {{ $Resources.NewEventIntroduction }}
            </p>
        </header>

        <div class="form-group">
            <button type="button" class="btn btn-cta p-lg-r p-lg-l" @click="onCreateEvent" :disabled="state.isLoading">
                {{ $Resources.EventNewEvent }}
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
        </div>

        <h2>{{ $Resources.EventsCopyTab }}</h2>
    </div>

    <EventList :selectedId="state.eventId" @clickedEventId="(newVal) => setEventId(newVal)"></EventList>

    <NewPreview :iframeSrc="iframeSrc" @close="state.eventId = null">
        <template #toolbar>
            <button type="button" class="btn btn-cta p-lg-r p-lg-l" @click="state.showSettingsModal = true" :disabled="state.isLoading">
                {{ $Resources.StartEditingBtn }}
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
        </template>
    </NewPreview>

    <Modal :title="$Resources.EventCopyModalHeading" :show="state.showSettingsModal" @close="state.showSettingsModal = false">
        <template #content>
            <p class="m-lg-b">{{ $Resources.EventCopyModalText }}</p>

            <Form
                :data="state.form.data"
                :disableSubmit="state.isLoading"
                @update:modelValue="(key, newVal) => (state.form!.data[key].modelValue = newVal)"
                @onSubmit="onCopyEvent">
                <template #button>
                    <button type="submit" class="btn btn-cta p-lg-r p-lg-l" :class="state.isLoading && 'is-busy'" :disabled="state.isLoading">
                        {{ $Resources.EventCopyBtnText }}
                    </button>
                </template>
            </Form>
        </template>
    </Modal>

    <Loading :show="state.isLoading" loadingStyle="progress-bar" :fixed="true"></Loading>
</template>

<style></style>

<style scoped></style>
